import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ResponsiveModalBottomSheet, LoadingOverlay } from '@/components';
import OTPVerificationView from '@/pages/components/OTPVerificationView';
import { BookingService } from '@/services';

interface OTPBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber: string;
  onSuccess: () => void;
}

const OTPBottomSheet: React.FC<OTPBottomSheetProps> = ({
  isOpen,
  onClose,
  phoneNumber,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [otpRef, setOtpRef] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isInitialRequestRef = useRef<boolean>(false);

  // Request OTP
  const requestOTP = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await BookingService.requestOTP(
        phoneNumber,
        'phoneNumber',
      );
      setOtpRef(response.ref);
      setExpiresAt(response.expiresAt);
    } catch (error) {
      console.error('Failed to request OTP:', error);
    } finally {
      setIsLoading(false);
    }
  }, [phoneNumber]);

  // Request OTP on mount
  useEffect(() => {
    if (isOpen) {
      isInitialRequestRef.current = true;
      requestOTP().finally(() => {
        isInitialRequestRef.current = false;
      });
    }
  }, [isOpen, requestOTP]);

  // Get last 3 digits of phone number
  const getLastThreeDigits = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.slice(-3);
  };

  const submitOTP = useCallback(
    async (code: string) => {
      const isValid = await BookingService.validateOTP(
        code,
        otpRef,
        phoneNumber,
      );
      if (isValid) {
        onSuccess();
      }
      return isValid;
    },
    [onSuccess, otpRef, phoneNumber],
  );

  const resendOTP = useCallback(async () => {
    await requestOTP();
  }, [requestOTP]);

  return (
    <ResponsiveModalBottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={t('package.otp.title')}
    >
      <div className="px-6 pt-2 pb-8">
        <OTPVerificationView
          illustrationSrc="/illustrations/otp-shield.png"
          title={t('package.otp.verifyMessage')}
          description={t('package.otp.sentTo', {
            last3: getLastThreeDigits(phoneNumber),
          })}
          refCode={otpRef}
          expiresAt={expiresAt}
          isOpen={isOpen}
          submitOTP={submitOTP}
          resendOTP={resendOTP}
          onLoading={setIsLoading}
        />
      </div>
      <LoadingOverlay isVisible={isLoading} />
    </ResponsiveModalBottomSheet>
  );
};

export default OTPBottomSheet;
